import type { User } from "@/store/useAuthStore";
import { hasRole } from "@/utils/role-utils";

/** UI context only. API responses and route guards remain the authority for access. */
export function classroomContextId(
  pathname: string,
  search: string,
): string | undefined {
  const routeId = /^\/classroom\/(\d+)$/.exec(pathname)?.[1];
  const queryId = pathname.startsWith("/spaces")
    ? new URLSearchParams(search).get("classroomId")
    : null;
  return positiveId(routeId ?? queryId);
}

/** The group a teacher has open on the Groups page (`/classrooms?group=`). */
export function teacherGroupId(
  pathname: string,
  search: string,
): string | undefined {
  return pathname === "/classrooms"
    ? positiveId(new URLSearchParams(search).get("group"))
    : undefined;
}

function positiveId(value: string | null | undefined): string | undefined {
  return value &&
    /^[1-9]\d*$/.test(value) &&
    Number.isSafeInteger(Number(value))
    ? value
    : undefined;
}

export function withClassroomContext(
  path: string,
  classroomId?: string,
): string {
  if (!classroomId) return path;
  const [pathname, search = ""] = path.split("?");
  const params = new URLSearchParams(search);
  params.set("classroomId", classroomId);
  return `${pathname}?${params.toString()}`;
}

export type NavSectionKey =
  | "home"
  | "spaces"
  | "sequencing"
  | "groups"
  | "dashboard";

/**
 * Input:   the current pathname and whether the viewer gets the teacher menu.
 * Output:  the one sidebar section the route belongs to, so child pages keep
 *          their section lit; undefined for routes the sidebar does not list.
 */
export function activeNavSection(
  pathname: string,
  isTeacherMenu: boolean,
): NavSectionKey | undefined {
  if (/^\/spaces(\/|$)/.test(pathname)) return "spaces";
  if (!isTeacherMenu) {
    return /^\/classrooms?(\/|$)/.test(pathname) ? "home" : undefined;
  }
  if (/^\/(lessons|curriculum)(\/|$)/.test(pathname)) return "home";
  if (pathname === "/lesson-sequencing") return "sequencing";
  if (pathname === "/classrooms") return "groups";
  if (pathname === "/analytics") return "dashboard";
  return undefined;
}

/** One breadcrumb step; `to` is absent on the current page and on labels. */
export interface Breadcrumb {
  label: string;
  to?: string;
}

/**
 * Input:   the route, the viewer's menu, and names only the shell knows:
 *          the open classroom or group (null while unknown) and the
 *          dashboard tab's label.
 * Output:  the trail from the sidebar section to the current page; empty for
 *          routes the workspace does not chart. The last step is the page.
 */
export function buildBreadcrumbs({
  pathname,
  search,
  isTeacherMenu,
  contextName,
  dashboardTabLabel,
}: {
  pathname: string;
  search: string;
  isTeacherMenu: boolean;
  contextName: string | null;
  dashboardTabLabel: string;
}): Breadcrumb[] {
  const isSpaceRecap = /^\/spaces\/[^/]+\/(student-)?recap$/.test(pathname);
  if (isTeacherMenu) {
    const home = { label: "Home", to: "/lessons" };
    const spaces = { label: "Spaces", to: "/spaces" };
    if (pathname === "/lessons") return [{ label: "Home" }];
    if (pathname === "/curriculum") return [home, { label: "Curriculum" }];
    if (/^\/curriculum\/[^/]+$/.test(pathname)) {
      return [
        home,
        { label: "Curriculum", to: "/curriculum" },
        { label: "Curriculum details" },
      ];
    }
    if (pathname === "/lesson-sequencing") {
      return [{ label: "Lesson sequencing" }];
    }
    if (pathname === "/classrooms") {
      const groupId = teacherGroupId(pathname, search);
      return groupId && contextName
        ? [{ label: "Groups", to: "/classrooms" }, { label: contextName }]
        : [{ label: "Groups" }];
    }
    if (pathname === "/analytics") {
      return [{ label: "Dashboard" }, { label: dashboardTabLabel }];
    }
    if (pathname === "/spaces") return [{ label: "Spaces" }];
    if (pathname === "/spaces/create") return [spaces, { label: "New Space" }];
    if (isSpaceRecap) return [spaces, { label: "Closed Space review" }];
    return [];
  }

  const classroomId = classroomContextId(pathname, search);
  const classroomLabel = contextName ?? "Your classroom";
  const classroomTrail: Breadcrumb[] = classroomId
    ? [
        { label: "Home", to: "/classrooms" },
        { label: classroomLabel, to: `/classroom/${classroomId}` },
      ]
    : [];
  if (pathname === "/classrooms") return [{ label: "Home" }];
  if (classroomId && pathname === `/classroom/${classroomId}`) {
    return [classroomTrail[0], { label: classroomLabel }];
  }
  if (pathname === "/spaces") return [...classroomTrail, { label: "Spaces" }];
  if (isSpaceRecap) {
    return [
      ...classroomTrail,
      { label: "Spaces", to: withClassroomContext("/spaces", classroomId) },
      { label: "Closed Space review" },
    ];
  }
  return [];
}

export function dashboardTabHref(search: string, tab: string): string {
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  return `/analytics?${params.toString()}`;
}

export type DashboardTabKey =
  | "analytics"
  | "standards"
  | "usage-control"
  | "admin";

/** The dashboard tabs this viewer gets, in display order. Analytics is always offered. */
export function visibleDashboardTabs(
  user: User | null | undefined,
): Array<{ key: DashboardTabKey; label: string }> {
  return [
    { key: "analytics", label: "Analytics" },
    { key: "standards", label: "Standards" },
    ...(user?.config?.showUsageDashboard
      ? [{ key: "usage-control" as const, label: "Usage control" }]
      : []),
    ...(hasRole(user?.role, "ORG_ADMIN") && user?.config?.enableAdminAnalytics
      ? [{ key: "admin" as const, label: "Admin" }]
      : []),
  ];
}

/** The ?tab= value when this viewer may see it, otherwise analytics. */
export function resolveDashboardTab(
  requested: string | null,
  user: User | null | undefined,
): DashboardTabKey {
  return (
    visibleDashboardTabs(user).find((tab) => tab.key === requested)?.key ??
    "analytics"
  );
}
